'use client';

import Link from 'next/link';
import { useTranslation } from '@/hooks/useTranslation';
import s from './Footer.module.css';
const COLS = [{
  title: 'Platform',
  titleKey: 'footer.col.platform',
  links: [['Social Intelligence', '/platform/social-intelligence'], ['Holistic Digital Care', '/platform/holistic-digital-care'], ['Market Research', '/platform/market-research'], ['Competitive Benchmarking', '/platform/competitive-benchmarking'], ['Social Media Management', '/platform/social-media-management'], ['Location Intelligence', '/platform/location-intelligence'], ['Command Centre', '/platform/command-centre'], ['Integrations', '/platform/integrations']]
}, {
  title: 'Resources',
  titleKey: 'footer.col.resources',
  links: [['Industry Reports', '/resources/industry-reports'], ['Customer Stories', '/resources/customer-stories'], ['Webinars', '/resources/webinars'], ['Guides & Ebooks', '/resources/guides-and-ebooks'], ['Events', '/resources/events'], ['Podcasts', '/resources/podcasts'], ['Blogs', 'https://locobuzz.com/blogs/']]
}, {
  title: 'Industries',
  titleKey: 'footer.col.industries',
  links: [['Telecom', '/industries/telecom'], ['Banking & Finance', '/industries/bfsi'], ['Retail', '/industries/retail-brands'], ['FMCG', '/industries/fmcg'], ['Healthcare', '/industries/healthcare'], ['Automobile', '/industries/mobility'], ['Travel & Hospitality', '/industries/travel-and-hospitality'], ['Technology', '/industries/technology']]
}, {
  title: 'Company',
  titleKey: 'footer.col.company',
  links: [['Partnerships', '/partnerships'], ['Pricing', '/pricing'], ['About Us', '/about'], ['Community Engagement', '/about/community-engagement'], ['L.E.A.P.', '/l-e-a-p'], ['Careers', '/careers'], ['Help Center', '/help-center'], ['Contact', '/contact'], ['Compliance', '/compliance'], ['Privacy Policy', '/privacy-policy'], ['Terms of Service', '/terms-of-service']]
}];

/* Compliance badges — drop the 4 PNGs the user shared at these paths:
   public/certs/soc2.webp, public/certs/iso27001.webp, public/certs/gdpr.png,
   public/certs/tac-security.png. Each badge auto-hides if its image is
   missing (broken-image fallback handled via onError). */
const CERTS = [{
  name: 'SOC 2 Type II',
  src: '/certs/soc2.webp'
}, {
  name: 'ISO 27001:2022',
  src: '/certs/iso27001.webp'
}, {
  name: 'GDPR Compliant',
  src: '/certs/gdpr.png'
}, {
  name: 'TAC Security — Verified',
  src: '/certs/tac-security.png'
}];
const SocialIcon = ({
  name,
  href,
  children
}) => <a href={href} target="_blank" rel="noopener noreferrer" title={name} className={s.social} aria-label={name}>
    <svg viewBox="0 0 24 24" fill="currentColor">{children}</svg>
  </a>;
export default function Footer() {
  const {
    t
  } = useTranslation();
  return <footer className={s.footer}>
      <div className={s.container}>

        {/* Top: brand col + 4 link cols */}
        <div className={s.top}>

          {/* Brand */}
          <div className={s.brand}>
            <Link href="/" className={s.brandLogo}>
              <img src="/locobuzz-logo.svg" alt={t("footer.locobuzz")} loading="lazy" decoding="async" />
            </Link>
            <p className={s.tagline}>{t('footer.tagline')}</p>

            <div className={s.socials}>
              <SocialIcon name="LinkedIn" href="https://www.linkedin.com/company/locobuzz">
                <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6z" />
                <rect x="2" y="9" width="4" height="12" />
                <circle cx="4" cy="4" r="2" />
              </SocialIcon>
              <SocialIcon name="Twitter / X" href="https://x.com/LocoBuzzIndia">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </SocialIcon>
              <SocialIcon name="Facebook" href="https://www.facebook.com/locobuzz">
                <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
              </SocialIcon>
              <SocialIcon name="Instagram" href="https://www.instagram.com/locobuzzsolutions">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5" fill="none" stroke="currentColor" strokeWidth="2" />
                <path fill="none" stroke="currentColor" strokeWidth="2" d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" />
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </SocialIcon>
              <SocialIcon name="YouTube" href="https://www.youtube.com/@LocobuzzIndia">
                <path d="M23 12s0-3.6-.46-5.32a2.51 2.51 0 00-1.77-1.77C19.05 4.45 12 4.45 12 4.45s-7.05 0-8.77.46A2.51 2.51 0 001.46 6.68C1 8.4 1 12 1 12s0 3.6.46 5.32a2.51 2.51 0 001.77 1.77C4.95 19.55 12 19.55 12 19.55s7.05 0 8.77-.46a2.51 2.51 0 001.77-1.77C23 15.6 23 12 23 12zM9.75 15.27V8.73L15.5 12z" />
              </SocialIcon>
            </div>

            <div className={s.contact}>
              <a href="mailto:sales@locobuzz.com" className={s.contactLink}>{t("footer.saleslocobuzzcom")}</a>
              <a href="tel:+912240305500" className={s.contactLink}>+91 22 4030 5500</a>
            </div>
          </div>

          {/* Link columns */}
          {COLS.map(col => <div key={col.title} className={s.col}>
              <div className={s.colHead}>{t(col.titleKey)}</div>
              {col.links.map(([label, href]) => href.startsWith('http') ? <a key={label} href={href} target="_blank" rel="noopener noreferrer" className={s.link}>{label}</a> : <Link key={label} href={href} className={s.link}>{label}</Link>)}
            </div>)}
        </div>

        {/* Compliance / Certificates strip */}
        <div className={s.compliance}>
          <span className={s.complianceLabel}>{t('footer.trusted')}</span>
          <div className={s.complianceBadges}>
            {CERTS.map(cert => <div key={cert.name} className={s.badge} title={cert.name}>
                <img src={cert.src} alt={cert.name} loading="lazy" decoding="async" onError={e => {
              e.currentTarget.style.display = 'none';
            }} />
              </div>)}
          </div>
        </div>

        {/* Bottom bar */}
        <div className={s.bottom}>
          <span className={s.copy} suppressHydrationWarning>
            © {new Date().getFullYear()} {t("footer.locobuzz_solutions_pvt_ltd")} {t('footer.copyright_suffix')}
          </span>
          <div className={s.legal}>
            <Link href="/privacy-policy/" className={s.legalLink}>{t('footer.legal.privacy')}</Link>
            <Link href="/terms-of-service/" className={s.legalLink}>{t('footer.legal.terms')}</Link>
            <Link href="/cookies-policy/" className={s.legalLink}>{t('footer.legal.cookies')}</Link>
            <Link href="/compliance/" className={s.legalLink}>{t('footer.legal.compliance')}</Link>
          </div>
        </div>
      </div>
    </footer>;
}